import React from 'react';
import { Link } from 'react-router-dom';
import { textareaToPlainText } from '../components';
import { Asset } from './';
// Direct import (not the './' barrel) to avoid a circular dependency through helpers/index.
import { DatasetSubtypeOperation } from './valueMap';

/**
 * Reduces a name to initials and returns a maximum of three characters
 * @param  {String} str  Full name
 * @return {String}      Properly formatted initials, maximum three characters.
 */
const _getInitials = str => {
  let names = str
    .split(/[ \-_.]/)
    .map(n => n[0])
    .join('')
    .toUpperCase();
  let initials =
    names.length > 3 ? names.slice(0, 2) + names.slice(names.length - 1) : names.slice(0, 3);
  return initials;
};

export const getInitials = _getInitials;

export const formatTooltip = ({
  type = 'string',
  title,
  tooltip,
  secondaryTooltip,
  value,
  values = {},
  label,
}) => {
  tooltip = !tooltip || tooltip === null ? undefined : textareaToPlainText(tooltip);

  switch (type) {
    case 'string':
      title = values && value ? values[value].label || title : title;
      break;
    case 'pill': {
      const mapped = values ? values[value] : undefined;
      // A pill's wording lives in its value map, and the map's tooltip is where a short label says
      // what it means — "NN" meaning Not Null. A tooltip passed by the caller still wins, so a
      // status pill goes on showing its own name above its description.
      // Read with a lookup rather than a truthiness test: `false` and `0` are values, and are
      // exactly the entries whose label needs explaining.
      title = (!tooltip && mapped?.tooltip) || mapped?.label || title;
      break;
    }
    case 'integer':
      // A row that has no number for this column shows "-" in the cell, so its tooltip says the
      // same. Formatting the missing value instead threw, or read "NaN%".
      title = tooltip || (title == null ? '-' : title.toLocaleString('sv-SE'));
      tooltip = title && tooltip ? undefined : tooltip;
      break;
    case 'percent':
      title = tooltip || (title == null ? '-' : (title * 100).toFixed(0) + '%');
      tooltip = title && tooltip ? undefined : tooltip;
      break;
    case 'boolean':
      title =
        tooltip ||
        values[title ? 'true' : 'false']?.tooltip ||
        values[title ? 'true' : 'false'].label;
      tooltip = undefined;

      break;
    case 'datetime':
      // Return string on the form yyyy-mm-dd hh:mm:ss
      title = new Date(title).toLocaleString('sv-SE');
      break;
    case 'icon':
      title = !tooltip ? title : tooltip;
      tooltip = undefined;
      break;
    case 'owner':
      title = title || 'Unknown';
      break;
    case 'chart':
      title = tooltip;
      tooltip = undefined;
      break;
    case 'button':
      title = label;
      break;
    case 'description':
      title = textareaToPlainText(title);
      tooltip = undefined;
      break;
    default:
      break;
  }

  /*if (title && !tooltip) {
      tooltip=title
      title=undefined
    }*/
  return { title: title, bodyText: tooltip, subTitle: secondaryTooltip };
};

/**
 * Formats a number value to a more user friendly version with unit prefix, i.e. "1500" gets formatted to "1.5k"
 * @param  {Integer} num Number to format
 * @return {String}      Aggregated number with prefix
 */
export const formatNumberUnit = num => {
  const lookup = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'k' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'G' },
    { value: 1e12, symbol: 'T' },
    { value: 1e15, symbol: 'P' },
    { value: 1e18, symbol: 'E' },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  var item = lookup
    .slice()
    .reverse()
    .find(function (item) {
      return num >= item.value;
    });
  return item
    ? parseFloat((num / item.value).toFixed(num / item.value >= 10 ? 0 : 1))
        .toLocaleString('sv-SE')
        .replace(rx, '$1') + item.symbol
    : '0';
};

/**
 * Formats a byte value to a more user friendly version with unit prefix, i.e. "1024" bytes gets formatted to "1kB"
 * @param  {Integer} bytes Byte number to format
 * @return {String}        Aggregated bytes with unit prefix
 */
export const formatDiskSize = bytes => {
  const lookup = [
    { value: 1, symbol: ' B' },
    { value: 1024, symbol: ' kB' },
    { value: 1024 ** 2, symbol: ' MB' },
    { value: 1024 ** 3, symbol: ' GB' },
    { value: 1024 ** 4, symbol: ' TB' },
    { value: 1024 ** 5, symbol: ' PB' },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  var item = lookup
    .slice()
    .reverse()
    .find(function (item) {
      return bytes >= item.value;
    });
  return item
    ? parseFloat((bytes / item.value).toFixed(bytes / item.value >= 10 ? 0 : 1))
        .toLocaleString('sv-SE')
        .replace(rx, '$1') + item.symbol
    : '0';
};

/**
 * Builds the CSS class suffix for an icon from a data value.
 *
 * Icons are styled by a class derived from a value the database supplies — a datatype category, a
 * dataset type name, a connector code. That derivation is used by `<Icon>`, by DataTable's `icon`
 * column type and by the search results, and it has to produce the same class in all of them: a
 * value that resolves two ways needs two CSS declarations, and the one that is missing renders as
 * an empty box rather than an error.
 *
 * Any run of characters that cannot appear in a class name collapses to a single dash, so
 * "Semantic Model", "bi_platform" and "Stored Procedure" all reduce cleanly.
 *
 * @param {string} value - the value to derive a class from
 * @returns {string} dash-separated, lowercased class suffix
 */
export const iconClassName = value =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

export function formatData(
  str,
  type = 'string',
  value,
  values = {},
  chartType,
  data,
  label,
  onClick
) {
  if (str == null && !(type === 'owner' || type === 'description')) {
    return str;
  }

  switch (type) {
    case 'string':
      return <span>{values && value ? values[value].label : str}</span>;
    case 'code':
      return <span className="code">{values && value ? values[value].label : str}</span>;
    case 'integer':
      return <span>{str.toLocaleString('sv-SE')}</span>;
    case 'integer-format':
      return <span>{formatNumberUnit(str)}</span>;
    case 'integer-disk-size':
      return <span>{formatDiskSize(str)}</span>;
    case 'percent':
      return <span>{(str * 100).toFixed(0) + '%'}</span>;
    case 'boolean':
      return (
        <span className={values[str ? 'true' : 'false'].className}>
          {values[str ? 'true' : 'false'].label}
        </span>
      );
    case 'datetime':
      if (str === undefined) return null;
      // Return string on the form yyyy-mm-dd hh:mm:ss
      return <span>{new Date(str).toLocaleString('sv-SE')}</span>;
    case 'icon':
      return <span className={'icon icon-' + iconClassName(str)}></span>;
    case 'owner':
      if (!str) return;
      //str = str ? str : 'Unknown'
      if (data?.photo) {
        return <img className="photo-img" src={'data:image/jpeg;base64,' + data.photo} alt={str} />;
      } else {
        return <div className={'icon photo-img placeholder icon-' + type}>{_getInitials(str)}</div>;
      }
    case 'pill':
      return <span className={values[value].className}>{values[value].label || str}</span>;
    case 'chart':
      switch (chartType) {
        case 'bar':
          return (
            <span className="chart-container">
              <span className="bar" style={{ width: (data.data / data.max) * 100 + '%' }}></span>
            </span>
          );
        case 'percent-bar':
          return (
            <span className="chart-container">
              <span className="bar" style={{ width: data.data * 100 + '%' }}></span>
              <span className="bar-bg" style={{ width: (1 - data.data) * 100 + '%' }}></span>
            </span>
          );
        default:
          break;
      }
      break;
    case 'button':
      return (
        <button className="button" onClick={() => onClick(data.data)}>
          <span>{label}</span>
        </button>
      );
    case 'description':
      return str ? textareaToPlainText(str) : <span className="no-result-text">-</span>;
    default:
      return str;
  }
}

export function formatConnectionString(
  database,
  hostname,
  parts,
  datasource_type_code,
  datasource_type_category
) {
  const delimiters = {
    api: ' > ',
    folder: '\\',
    database: '.',
    tableau_server: '/',
  };

  const enclosures = {
    api: { start: '', end: '' },
    folder: { start: '', end: '' },
    mssql: { start: '', end: '' }, //mssql: { start: "[", end: "]"},
    postgres: { start: '', end: '' }, //postgres: { start: "\"", end: "\""},
    snowflake: { start: '', end: '' }, //snowflake: { start: "\"", end: "\""},
    oracle: { start: '', end: '' }, //oracle: { start: "\"", end: "\""},
    ibmdb2: { start: '', end: '' }, //ibmdb2: { start: "\"", end: "\""},
    tableau: { start: '', end: '' }, //tableau: { start: "", end: ""}
  };

  const enclosureStart =
    (enclosures[datasource_type_code] && enclosures[datasource_type_code].start) || '';
  const enclosureEnd =
    (enclosures[datasource_type_code] && enclosures[datasource_type_code].end) || '';

  const delimiter = delimiters[datasource_type_category] || '.';

  const allParts = [database ? database : undefined, ...parts];

  const conn_string = allParts.map((part, key) => {
    var fragment = part ? enclosureStart + part + enclosureEnd : '';
    if (key !== allParts.length - 1 && part) {
      fragment = fragment + delimiter;
    }

    return (
      <span className="word" key={key}>
        {fragment}
      </span>
    );
  });

  return conn_string;
}

/**
 * The subtype to show beside a dataset's name, or nothing when it would only repeat the type.
 *
 * A subtype earns its place when it draws a distinction its type does not: an endpoint's method
 * separates GET /users from POST /users, so "GET users" says something the type name cannot. The
 * Power BI kinds are the other case — each is the only subtype of its own dataset type, sharing its
 * code, so appending it would render "Report Sales Report" and say the same thing twice.
 *
 * Compared on the codes, not the display names: the names are free to be reworded, the codes are
 * the identity. Data-driven rather than a check for which connector the dataset came from, so a
 * future source with meaningful subtypes gets this right without a change here.
 *
 * @param {string} [subtypeCode] - The dataset's dataset_subtype_code.
 * @param {string} [subtypeName] - The dataset's dataset_subtype_name.
 * @param {string} [typeCode] - The dataset's dataset_type_code.
 * @returns {string|undefined} The subtype name when it adds something, otherwise undefined.
 */
export function datasetSubtypeLabel(subtypeCode, subtypeName, typeCode) {
  return subtypeCode && subtypeCode !== typeCode ? subtypeName : undefined;
}

/**
 * Builds a dataset's display name, prefixing the API method when one is present.
 * e.g. formatDatasetName('users', 'GET') -> 'GET users'
 * @param {string} datasetName - The dataset's name.
 * @param {string} [datasetSubtypeName] - HTTP method name for API datasets; omitted otherwise.
 * @returns {string} The dataset name, prefixed with the API method when given.
 */
export function formatDatasetName(datasetName, datasetSubtypeName) {
  return datasetSubtypeName ? `${datasetSubtypeName} ${datasetName}` : datasetName;
}

/**
 * Atomic renderer for a single dataset shown as "method + name". This is the one place that
 * decides how a dataset's API method is displayed alongside its name.
 *
 * - Returns a plain string (via formatDatasetName) when neither a pill nor a link is requested —
 *   for text contexts that compose through formatConnectionString (subtitles) or need a string
 *   (cytoscape labels).
 * - Returns JSX otherwise: the method as a colored pill span (CSS classes from DatasetSubtypeOperation,
 *   no Pill component, so no per-row hooks), followed by the name, optionally wrapped in a <Link>.
 *
 * @param {string} datasetName - The dataset's name (or source URL) to show.
 * @param {string} [datasetSubtypeName] - HTTP method; rendered as a pill/prefix when present.
 * @param {object} [opts]
 * @param {string} [opts.datasetSubtypeOperation] - Operation key for the pill colour (DatasetSubtypeOperation).
 * @param {boolean} [opts.asPill=false] - Render the method as a colored pill span (else text/none).
 * @param {string} [opts.to] - When set, wrap the segment in a router <Link> to this path.
 * @returns {string|JSX.Element} A string for the plain text form, otherwise JSX.
 */
export function formatDatasetSegment(
  datasetName,
  datasetSubtypeName,
  { datasetSubtypeOperation, asPill = false, to, className } = {}
) {
  // Plain text form — used by inline subtitles and string-only contexts.
  if (!asPill && !to && !className) {
    return formatDatasetName(datasetName, datasetSubtypeName);
  }

  const method =
    datasetSubtypeName &&
    (asPill ? (
      <span
        className={`${DatasetSubtypeOperation[datasetSubtypeOperation]?.className || 'pill'} pill-slim`}
      >
        {datasetSubtypeName}
      </span>
    ) : (
      `${datasetSubtypeName} `
    ));

  const content = (
    <React.Fragment>
      {method}
      {datasetName}
    </React.Fragment>
  );

  // `className` is applied to the segment element itself (the <a>/<span>), not a wrapper, so it
  // wins over the base `a` rule's color/font-size (a class beats the element selector). Putting it
  // on an ancestor instead would only inherit, which the <a>'s own declared values override.
  return to ? (
    <Link className={'link' + (className ? ' ' + className : '')} to={to}>
      {content}
    </Link>
  ) : (
    <span className={'dataset-path-segment' + (className ? ' ' + className : '')}>{content}</span>
  );
}

/**
 * Builds a dataset's hierarchical path for table cells: the dataset itself, then its ancestors
 * (root chain), each a linked name and stacked below it. Depth-independent — it walks the supplied
 * ancestors array — so it reads the same for an API endpoint's response schema and for a table
 * inside a Power BI semantic model, and a dataset with no ancestors renders as a single linked
 * name. Returns JSX (no React component, so it does not add a per-row reconciliation boundary).
 *
 * Only an API dataset's segments carry a method pill: there the subtype is the HTTP method and says
 * something the name does not, while every other kind's subtype merely repeats its dataset type.
 *
 * @param {object} data - The row: dataset_id, dataset_name, datasource_type_category,
 *   dataset_type_code, dataset_subtype_name, dataset_subtype_operation, and system_id/datasource_id/
 *   dataset_group_id (used to build the segment links).
 * @param {object} [opts]
 * @param {object[]} [opts.ancestors] - Ancestor datasets root -> immediate parent.
 * @returns {JSX.Element} The stacked path.
 */
export function formatDatasetPath(data, { ancestors = [] } = {}) {
  const linkTo = id =>
    `/systems/${data.system_id}/${data.datasource_id}/${data.dataset_group_id}/${id}`;
  const isApi = data.datasource_type_category === 'api';
  const isEndpoint = isApi && data.dataset_type_code === 'api_endpoint';

  // Leaf (this dataset) first, then its ancestors below it. Every nesting source is walked, not
  // only APIs: a table's name alone says nothing about which semantic model holds it, and two
  // models in one workspace routinely carry a table of the same name.
  const segments = [
    {
      datasetId: data.dataset_id,
      name: data.dataset_name,
      method: isEndpoint ? data.dataset_subtype_name : null,
      operation: isEndpoint ? data.dataset_subtype_operation : null,
    },
    ...ancestors.map(a => ({
      datasetId: a.dataset_id,
      name: a.dataset_name,
      method: isApi ? a.dataset_subtype_name : null,
      operation: isApi ? a.dataset_subtype_operation : null,
    })),
  ];

  return (
    <span className="dataset-path">
      {segments.map((segment, index) => (
        <span className="dataset-path-segment" key={segment.datasetId ?? index}>
          {formatDatasetSegment(segment.name, segment.method, {
            datasetSubtypeOperation: segment.operation,
            asPill: true,
            to: linkTo(segment.datasetId),
            // Ancestor segments are de-emphasised. small-text must sit on the <a> itself (not a
            // wrapper) to beat the base `a` rule's color/font-size — see formatDatasetSegment.
            className: index > 0 ? 'small-text' : undefined,
          })}
        </span>
      ))}
    </span>
  );
}

/**
 * Builds the subtitle path for a dataset (optionally a field within it), used by the
 * Dataset view, Field view and search results.
 *
 * For API datasources, behaviour depends on the dataset's type:
 *   - an API Endpoint (dataset_type_code 'api_endpoint'): just its source URL (the method is
 *     shown as a pill in the title), with no datasource prefix.
 *   - anything else (a schema, or a field within one): the datasource prefix is kept, the
 *     parent endpoint is shown as `datasetSubtype + (source_url || dataset_name)` (e.g.
 *     `GET /api/users`), followed by this dataset's own name.
 * For non-API datasources: the standard database/group/name connection string.
 *
 * @param {object} data - A dataset-like record (a view `data` object or a search `searchable`).
 * @param {object} [opts]
 * @param {object[]} [opts.ancestors] - Ancestor datasets ordered root->parent, each carrying
 *   dataset_name, dataset_source_url and dataset_subtype_name.
 * @param {string[]} [opts.trailingParts] - Extra trailing path parts, e.g. [field_name].
 * @returns {JSX.Element[]} The spans produced by formatConnectionString.
 */
export function formatDatasetSubtitle(data, { ancestors = [], trailingParts = [] } = {}) {
  const isApi = data.datasource_type_category === 'api';
  const isApiEndpoint = isApi && data.dataset_type_code === 'api_endpoint';

  let parts;
  let database = data.datasource_database;
  if (isApiEndpoint) {
    // Endpoint: its source URL alone, and drop the datasource prefix.
    database = undefined;
    const leaf =
      data.dataset_source_url || formatDatasetSegment(data.dataset_name, data.dataset_subtype_name);
    parts = [leaf, ...trailingParts];
  } else if (isApi) {
    // Schema (or a field within one): keep the datasource prefix; show each ancestor endpoint
    // as method + name (plain text), then this dataset's own name.
    const path = ancestors.map(a => formatDatasetSegment(a.dataset_name, a.dataset_subtype_name));
    path.push(data.dataset_name);
    parts = [path.join(' > '), ...trailingParts];
  } else {
    parts = [data.dataset_group_name, data.dataset_name, ...trailingParts];
  }

  return formatConnectionString(
    database,
    data.datasource_hostname,
    parts,
    data.datasource_type_code,
    data.datasource_type_category
  );
}

// Function to substitute a template string with placeholders with actual datapoints
// Mainly used by the DataTable component
export function compileTemplateString(templateString, data) {
  let re = new RegExp(':(([a-zA-Z0-9_])*)/?', 'gi');

  let matches = [];
  let params = [];

  while ((matches = re.exec(templateString))) {
    params.push(matches[1]);
  }

  let map = {};
  params.forEach(match => {
    map = { ...map, [':' + match]: data[match] };
  });

  re = new RegExp(Object.keys(map).join('|'), 'gi');

  let compiledString = templateString;

  // If the template string contains placeholders, substitute them with data
  if (params.length > 0) {
    compiledString = templateString.replace(re, function (matched) {
      return map[matched];
    });
  }

  return compiledString === 'undefined' ? undefined : compiledString;
}

// Function to add "https://"-prefix to strings if not already present
export function renderUrl(str) {
  return !str.match(/^https?:\/\//) ? `https://${str}` : str;
}

/**
 * The syntax to highlight an asset's technical definition with.
 *
 * The language follows the datasource, and for Power BI also the asset: a semantic model's table is
 * loaded by M (Power Query) while a measure is written in DAX, so the same datasource needs both.
 * Returns undefined where there is nothing better to say, which leaves TextareaCode on its default
 * of SQL -- right for the database connectors, and harmless elsewhere.
 *
 * @param {string} [datasourceTypeCode]
 * @param {string} [assetName] - an `Asset` value; only the Field/Dataset distinction matters
 * @param {string} [datasetTypeCode] - the dataset's type, which tells a calculated table apart
 * @returns {string|undefined}
 */
export function technicalDefinitionLanguage(datasourceTypeCode, assetName, datasetTypeCode) {
  switch (datasourceTypeCode) {
    case 'api':
      return 'json';
    case 'powerbi':
      // A field is always DAX -- a measure or a calculated column. A dataset is the M that loads
      // it, except a calculated table, whose rows come from a DAX expression rather than a query.
      if (assetName === Asset.Field) return 'dax';

      return datasetTypeCode === 'calculated_table' ? 'dax' : 'powerquery';
    default:
      return undefined;
  }
}

/**
 * What to call a technical definition's language on screen.
 *
 * Kept beside technicalDefinitionLanguage so the two stay in step: a language added there needs a
 * label here, and the fallback is the code itself upper-cased rather than a wrong name.
 *
 * @param {string} [language] - a value from technicalDefinitionLanguage, or undefined for the
 *   editor's default
 * @returns {string}
 */
export function technicalDefinitionLanguageLabel(language) {
  switch (language) {
    case undefined:
    case 'sql':
      return 'SQL';
    case 'json':
      return 'JSON';
    case 'dax':
      return 'DAX';
    case 'powerquery':
      return 'M';
    default:
      return String(language).toUpperCase();
  }
}

/**
 * Connectors whose assets are commonly opened in the source system, and so get a button of their
 * own rather than only the Source URL row. Deliberately a short list: the button is worth the space
 * where following the link is part of the daily work -- reading a Power BI report, opening a
 * Databricks table -- and clutter everywhere else.
 */
export const CONNECTORS_WITH_SOURCE_LINK = ['powerbi', 'databricks'];

/**
 * The "open this asset in its source system" action, or null when there is nothing worth showing.
 *
 * Null rather than a disabled button: an asset with no source link, or one from a connector where
 * following the link is not a common action, should show no button at all. The Source URL detail
 * row still carries the value in those cases.
 *
 * @param {string} [url] - the asset's source url, e.g. dataset_source_url
 * @param {string} [connectorCode] - the asset's connector_code
 * @param {string} [connectorName] - the asset's connector_name, for the label
 * @returns {{ href: string, label: string, tooltip: string }|null}
 */
export function sourceLinkAction(url, connectorCode, connectorName) {
  if (!CONNECTORS_WITH_SOURCE_LINK.includes(connectorCode)) return null;
  if (!isLinkableUrl(url)) return null;

  const target = connectorName || 'source';

  return {
    href: renderUrl(url),
    label: `Open in ${target}`,
    tooltip: `Open this asset in ${target}`,
  };
}

/**
 * Whether a value can be turned into a working link by renderUrl.
 *
 * Columns like dataset_source_url hold "a URL or a path", and a connector decides which: Power BI
 * stages an absolute link, Databricks stages a storage path. renderUrl prefixes anything without a
 * scheme with "https://", which turns a path into the nonsense `https:///mnt/data`, so a caller
 * that renders links has to ask this first and fall back to plain text.
 *
 * A value that names its own scheme is taken at its word. One that does not has to look like a
 * host -- a dotted domain, or localhost -- so that "app.powerbi.com/groups/x" and a local database
 * host still link while "/mnt/data", "C:\\export" and a bare word do not.
 *
 * @param {*} value
 * @returns {boolean}
 */
export function isLinkableUrl(value) {
  if (typeof value !== 'string') return false;

  const str = value.trim();
  if (!str || /\s/.test(str)) return false;

  // Paths, whether posix, UNC or Windows-drive.
  if (/^[/\\]/.test(str) || /^[a-zA-Z]:[/\\]/.test(str)) return false;

  const hasScheme = /^https?:\/\//i.test(str);

  try {
    const url = new URL(hasScheme ? str : `https://${str}`);
    // A dotted host, or localhost -- the one bare word that is unambiguously a host rather than a
    // stray value, and what a connection to a local database is called.
    return hasScheme || url.hostname.includes('.') || url.hostname === 'localhost';
  } catch {
    return false;
  }
}

// Function to compile an array with strings into a string on the format "element1 > element2 > ... > elementN"
export function formatPath(parts, delimiter = ' > ') {
  let path = '';
  parts.forEach((part, i) => {
    path = path + (i > 0 ? `${delimiter}` : '') + part;
  });

  return path;
}

/**
 * Recursively parse path for hierarchial items.
 *
 * A row's name is read from its own kind first (`object_name` + "_name"), which is what lets one
 * chain cross from one kind to another: a value under an attribute under an entity, or a field under
 * a field under a dataset. Without that a mixed chain reads as the type words — "attribute_value >
 * attribute > Customer" — because every row would be asked for the walked kind's name column.
 *
 * @param {object} item                  Current row
 * @param {object[]|Map} items           Every row that can appear in the chain. A Map keyed by the
 *                                       id the rows' parent pointers reference avoids the lookup
 *                                       scan, which matters when a batch of rows is resolved at once.
 * @param {string} [objectName]          The kind the chain is walked as, for rows that do not
 *                                       declare their own
 * @param {string} [concatenator]
 * @return {string} String on the form "parent.child.grandchild. ..."
 * */
export const getHierarchyPath = (item, items, objectName = Asset.Field, concatenator) => {
  if (!item) return '';
  const concat = concatenator || (objectName === Asset.Entity ? ' > ' : '.');
  // The row's own kind wins: in a list holding several, the walked kind names only some of them.
  let path = item[`${item.object_name}_name`] || item[`${objectName}_name`] || item[`object_name`];
  let parentId =
    item[`parent_unique_id`] || item[`parent_${objectName}_id`] || item[`parent_object_id`];
  if (parentId) {
    const parentItem =
      items instanceof Map
        ? items.get(parentId)
        : items.find(x => (x[`unique_id`] || x[`${objectName}_id`] || x[`object_id`]) === parentId);

    // A parent that is not in the list ends the chain here rather than throwing: a row can be shown
    // without its ancestors, as a search match is before the rows above it are read.
    if (!parentItem) return path;

    // Prefix the parent dataset's API method via the shared atomic (e.g. "GET ") so the path text
    // matches how the method is shown everywhere else.
    const methodPrefix =
      objectName === Asset.Dataset ? formatDatasetName('', parentItem.dataset_subtype_name) : '';
    path =
      methodPrefix + getHierarchyPath(parentItem, items, objectName, concatenator) + concat + path;
  }
  return path;
};

/**
 * Formats CamelCase and snake_case strings to a whitespace-separated, capitalized string, like "snake_case" => "Snake Case"
 * @param  {String} str String in CamelCase or snake_case format
 * @return {String}     Nicely formated whitespace-separated and capitalized string
 */
/**
 * Pluralises a display label.
 *
 * Appending "s" is not enough for the labels this is asked for: it produces "Entitys", "Glossarys",
 * "Statuss" and "Data Sensitivity Classs". These are the two English rules those need — consonant + y
 * becomes -ies, and a sibilant ending takes -es — which between them cover every label it is given.
 *
 * @param {string} label - A singular label.
 * @param {number} count - How many; 1 returns the label unchanged.
 * @returns {string}
 */
export const pluralize = (label, count) => {
  if (count === 1) return label;
  if (/(?:[sxz]|ch|sh)$/i.test(label)) return `${label}es`;
  if (/[^aeiou]y$/i.test(label)) return `${label.slice(0, -1)}ies`;
  return `${label}s`;
};

export function formatUserFriendly(str) {
  if (!str) {
    return '';
  }

  str = str.replace(/[_\s]+/g, ' ').trim();

  let niceString = '';

  // Handle differently based on if input string is CamelCase or not
  if (str.match(/[a-z]/) && str.match(/[A-Z]/)) {
    // is CamelCase
    niceString = str
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/([A-Z])([a-z])/g, ' $1$2')
      .replace(/ +/g, ' ')
      .trim();

    niceString = niceString[0].toUpperCase() + niceString.slice(1);
  } else {
    // is not CamelCase
    str = str.split(/[\s]/);
    str.forEach((word, i) => {
      niceString =
        (i !== 0 ? niceString + ' ' : '') +
        (word[0] !== undefined ? word[0].toUpperCase() : '') +
        word.slice(1).toLowerCase();
    });
  }

  return niceString;
}

// Function to format seconds to the more user friendly format "hours:minutes:seconds"
export function formatDuration(sec) {
  let hours = Math.floor((sec %= 86400) / 3600) || '0';
  if (hours) {
    hours = hours < 10 ? `0${hours}` : hours;
  }
  let minutes = Math.floor((sec %= 3600) / 60) || '0';
  if (minutes) {
    minutes = minutes < 10 ? `0${minutes}` : minutes;
  }
  let seconds = (sec % 60).toFixed(0) || '0';
  if (seconds) {
    seconds = seconds < 10 ? `0${seconds}` : seconds;
  }

  let duration = `${hours}:${minutes}:${seconds}`;

  return duration;
}
